import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { BOOKING_URL, NAV_LINKS, goToLink } from "@/lib/site";
import { useLang } from "@/i18n";
import { EASE } from "@/components/Reveal";
import { IMAGES } from "@/content/images";

const Logo = ({ light, onNavigate }) => (
  <a
    href="/"
    data-testid="nav-logo"
    onClick={(e) => { e.preventDefault(); onNavigate(); }}
    className="block shrink-0"
  >
    <img
      src={light ? IMAGES.logos.white : IMAGES.logos.golden}
      alt="Cabo Yoga & Fitness"
      className="h-11 w-auto transition-[opacity,transform] duration-500 sm:h-12 md:h-14 lg:h-16"
    />
  </a>
);

const LangToggle = ({ light = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-toggle" className={`flex shrink-0 items-center gap-1.5 label-caps sm:gap-2 ${light ? "text-ivory/70" : "text-charcoal/50"}`}>
      {["en", "es"].map((l, i) => (
        <span key={l} className="flex items-center gap-1.5 sm:gap-2">
          {i > 0 && <span className={`h-3 w-px ${light ? "bg-ivory/30" : "bg-charcoal/20"}`} />}
          <button
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`transition-colors duration-300 ${
              lang === l ? (light ? "text-ivory" : "text-charcoal") : light ? "hover:text-ivory" : "hover:text-charcoal"
            } ${lang === l ? "underline underline-offset-4 decoration-gold" : ""}`}
          >
            {l.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const isTransparent = pathname === "/" && !scrolled && !open;
  const light = isTransparent;

  const go = (e, link) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => goToLink(link, { pathname, navigate }), open ? 350 : 0);
  };

  const goHome = () => {
    setOpen(false);
    goToLink({ href: "#top" }, { pathname, navigate });
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          isTransparent
            ? "border-b border-ivory/10 bg-gradient-to-b from-espresso/35 via-espresso/16 to-transparent backdrop-blur-[2px]"
            : "bg-ivory/90 backdrop-blur-md border-b border-charcoal/10"
        }`}
      >
        <div className="mx-auto grid max-w-[1600px] grid-cols-[minmax(0,1fr)_auto] items-start px-4 py-3 sm:px-6 md:flex md:items-center md:justify-between md:px-12 md:py-5">
          <Logo light={light} onNavigate={goHome} />
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href.startsWith("#") ? `/${l.href}` : l.href} data-testid={`nav-link-${l.key}`} onClick={(e) => go(e, l)}
                className={`u-link label-caps transition-colors duration-500 ${light ? "text-ivory/90 hover:text-ivory" : "text-charcoal/70 hover:text-charcoal"}`}>
                {t.nav[l.key]}
              </a>
            ))}
            <LangToggle light={light} />
            <a href={BOOKING_URL} data-testid="nav-book-button" className="btn-pill bg-charcoal text-ivory hover:bg-espresso !py-3 !px-7">
              {t.nav.schedule}
            </a>
          </nav>
          <div className="grid shrink-0 grid-cols-[auto_auto] items-center justify-items-end gap-x-2 gap-y-1 md:flex md:items-center md:gap-3 lg:hidden">
            <LangToggle light={light} />
            <a href={BOOKING_URL} data-testid="nav-book-button-mobile" className="col-span-2 row-start-2 justify-self-end btn-pill whitespace-nowrap !px-2.5 !py-2.5 text-[9px] leading-none bg-charcoal text-ivory sm:!px-4 sm:text-[10px] md:col-auto md:row-auto">
              {t.nav.schedule}
            </a>
            <button data-testid="nav-menu-toggle" aria-label="Open menu" onClick={() => setOpen(true)} className={`col-start-2 row-start-1 p-0.5 transition-colors duration-500 sm:p-1 md:col-auto md:row-auto ${light ? "text-ivory" : "text-charcoal"}`}>
              <Menu size={26} strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[60] bg-ivory flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo light={false} onNavigate={goHome} />
              <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)} className="p-1 text-charcoal">
                <X size={28} strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href} href={l.href.startsWith("#") ? `/${l.href}` : l.href} data-testid={`mobile-nav-link-${l.key}`} onClick={(e) => go(e, l)}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.7, ease: EASE }}
                  className="group flex items-baseline justify-between border-b border-charcoal/10 py-5"
                >
                  <span className="font-display text-4xl text-charcoal">{t.nav[l.key]}</span>
                  <ArrowUpRight size={20} strokeWidth={1.25} className="text-taupe transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </motion.a>
              ))}
            </nav>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="px-8 pb-10">
              <a href={BOOKING_URL} data-testid="mobile-menu-book-button" className="btn-pill btn-primary w-full">
                {t.nav.book}
              </a>
              <p className="mt-6 text-center label-caps text-charcoal/40">Cabo San Lucas, Baja California Sur</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
